// TypeScript
import { useMemo } from 'react'
import { CatalogItemType, getWrappedMacro, Macro } from '@pushly/models/lib/enums'
import {
    getStrategyOptionsForCatalogItemType,
    CatalogItemPickStrategy,
    type CombinedItemStrategy,
    getRankingOnlyOptions,
    ItemRankingStrategy,
} from '@pushly/models/lib/enums/catalogs'
import {
    ActionStep,
    isSendCartNotificationActionStep,
    isSendSavedItemNotificationActionStep,
    isSendItemNotificationActionStep,
    isSendViewedItemNotificationActionStep,
    type JourneyNode,
} from '../types/journey-nodes'
import {
    getPriorSendActionCount,
    computeCatalogItemPickStrategyOptions,
    reconcileSelectedStrategy,
} from '../selectors/catalog-item-pick-strategy'

export type ItemStrategyTemplates = {
    url: string
    title: string
    body: string
    image: string
    imageMacroTag: string
    imageMacroTagLabel: string
}

export type ItemStrategyParams = {
    action: ActionStep
    itemType?: CatalogItemType
    allNodes?: JourneyNode[]
    currentNodeId?: string
    currentSelectedStrategy?: CombinedItemStrategy
}

export type ItemStrategyState = {
    isCatalog: boolean
    templates: ItemStrategyTemplates
    options: CombinedItemStrategy[]
    selected: CombinedItemStrategy | undefined
}

function buildTemplatesAndBaseOptions(itemType?: CatalogItemType): {
    templates: ItemStrategyTemplates
    baseOptions: CatalogItemPickStrategy[]
} {
    const templates: ItemStrategyTemplates = {
        url: getWrappedMacro(Macro.ECOMM_ITEM_URL),
        title: getWrappedMacro(Macro.ECOMM_ITEM_NAME),
        body: getWrappedMacro(Macro.ECOMM_ITEM_DESCRIPTION, { defaultValue: '' }),
        image: getWrappedMacro(Macro.ECOMM_ITEM_IMAGE),
        imageMacroTag: Macro.ECOMM_ITEM_IMAGE,
        imageMacroTagLabel: 'Use Item Image',
    }

    const baseOptions = getStrategyOptionsForCatalogItemType(
        itemType ?? CatalogItemType.PRODUCT,
    ) as CatalogItemPickStrategy[]

    return { templates, baseOptions }
}

export function computeItemStrategyState(params: ItemStrategyParams): ItemStrategyState {
    const { action, itemType, allNodes, currentNodeId, currentSelectedStrategy } = params
    const isCatalog =
        isSendCartNotificationActionStep(action) ||
        isSendSavedItemNotificationActionStep(action) ||
        isSendViewedItemNotificationActionStep(action) ||
        isSendItemNotificationActionStep(action)

    const { templates, baseOptions } = buildTemplatesAndBaseOptions(itemType)

    if (!isCatalog) {
        return { isCatalog, templates, options: [], selected: undefined }
    }

    // Only show ranking strategies (Popular options, no context-based strategies)
    // Filter by itemType to exclude unsupported strategies (e.g., carted/purchased for recipes)
    if (isSendItemNotificationActionStep(action)) {
        const rankingOptions = getRankingOnlyOptions(itemType)
        const options = rankingOptions.map(opt => opt.value)
        const selected = currentSelectedStrategy && options.includes(currentSelectedStrategy)
            ? currentSelectedStrategy
            : (options.length > 0 ? options[0] : ItemRankingStrategy.VIEWED_RANK_7D)

        return { isCatalog, templates, options, selected }
    }

    // Only show context-based strategies (Personalized options, no ranking strategies)
    if (isSendSavedItemNotificationActionStep(action)) {
        const priorCount = getPriorSendActionCount(allNodes ?? [], currentNodeId ?? '', action.meta?.actionIndex)
        const filteredBaseOptions = computeCatalogItemPickStrategyOptions(baseOptions, priorCount)
        const options = filteredBaseOptions as CombinedItemStrategy[]

        // Reconcile selected strategy - prefer current if valid, otherwise default to most_recent
        const selected = currentSelectedStrategy && options.includes(currentSelectedStrategy)
            ? currentSelectedStrategy
            : (filteredBaseOptions.length > 0
                ? (filteredBaseOptions.includes(CatalogItemPickStrategy.MOST_RECENT)
                    ? CatalogItemPickStrategy.MOST_RECENT
                    : filteredBaseOptions[0])
                : CatalogItemPickStrategy.MOST_RECENT)

        return { isCatalog, templates, options, selected }
    }

    // Only show context-based strategies (Personalized options)
    const priorCount = getPriorSendActionCount(allNodes ?? [], currentNodeId ?? '', action.meta?.actionIndex)
    const filteredBaseOptions = computeCatalogItemPickStrategyOptions(baseOptions, priorCount)
    const options = filteredBaseOptions as CombinedItemStrategy[]

    // Reconcile selected strategy - prefer current if valid, otherwise default to most_recent
    const selected = currentSelectedStrategy && options.includes(currentSelectedStrategy)
        ? currentSelectedStrategy
        : (filteredBaseOptions.length > 0
            ? (filteredBaseOptions.includes(CatalogItemPickStrategy.MOST_RECENT)
                ? CatalogItemPickStrategy.MOST_RECENT
                : filteredBaseOptions[0])
            : CatalogItemPickStrategy.MOST_RECENT)

    return { isCatalog, templates, options, selected }
}

export function useItemStrategyOptions(params: ItemStrategyParams): ItemStrategyState {
    return useMemo(
        () => computeItemStrategyState(params),
        [
            params.action,
            params.action?.meta?.actionIndex,
            params.itemType,
            params.allNodes,
            params.currentNodeId,
            params.currentSelectedStrategy,
        ],
    )
}
